
import React, { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import CommentEditor from '@/components/community/CommentEditor';
import CommentActions from '@/components/community/CommentActions';
import CommentHeader from '@/components/community/CommentHeader';
import CommentTopics from '@/components/community/CommentTopics';
import { CommentType } from '@/components/reader-home/types';

interface CommentProps {
  comment: CommentType;
  isReply?: boolean;
  nestLevel?: number;
  onReply: (commentId: number, content: string) => void;
}

const CommentItem: React.FC<CommentProps> = ({ 
  comment, 
  isReply = false, 
  nestLevel = 0,
  onReply 
}) => {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [likeCount, setLikeCount] = useState(comment.likes);
  const [dislikeCount, setDislikeCount] = useState(comment.dislikes);
  const [saved, setSaved] = useState(false);

  // Calculate indentation based on nest level, with maximum of 3 levels
  const indentationLevel = Math.min(nestLevel, 3);
  const marginClass = indentationLevel > 0 ? `ml-${indentationLevel * 4}` : '';

  const handleLike = () => {
    if (liked) {
      setLikeCount(likeCount - 1);
      setLiked(false);
    } else {
      if (disliked) {
        setDislikeCount(dislikeCount - 1);
        setDisliked(false);
      }
      setLikeCount(likeCount + 1);
      setLiked(true);
    }
  };

  const handleDislike = () => {
    if (disliked) {
      setDislikeCount(dislikeCount - 1);
      setDisliked(false);
    } else {
      if (liked) {
        setLikeCount(likeCount - 1);
        setLiked(false);
      }
      setDislikeCount(dislikeCount + 1);
      setDisliked(true);
    }
  };

  const handleReplySubmit = (content: string) => {
    onReply(comment.id, content);
    setShowReplyForm(false);
  };

  return (
    <div className={`mb-6 ${isReply ? (nestLevel === 1 ? 'ml-12' : marginClass) : ''}`}>
      <div className="flex gap-3">
        <div>
          <Avatar className="h-10 w-10">
            <AvatarImage src={comment.user.avatar} alt={comment.user.name} />
            <AvatarFallback>{comment.user.name.substring(0, 2)}</AvatarFallback>
          </Avatar>
        </div>
        
        <div className="flex-1">
          <div className={cn(
            "bg-accent/30 rounded-lg p-3",
            comment.highlighted && "border-l-4 border-primary"
          )}>
            <CommentHeader
              avatar={comment.user.avatar}
              name={comment.user.name}
              isJournalist={comment.user.isJournalist}
              highlighted={comment.highlighted}
              timestamp={comment.timestamp}
              bio={comment.user.bio}
              joinDate={comment.user.joinDate}
            />
            
            <p className="text-sm whitespace-pre-line">{comment.content}</p>
            
            <CommentTopics topics={comment.topics} />
          </div>
          
          <CommentActions
            liked={liked}
            disliked={disliked}
            likeCount={likeCount}
            dislikeCount={dislikeCount}
            saved={saved}
            isReply={isReply}
            onLike={handleLike}
            onDislike={handleDislike}
            onReply={() => setShowReplyForm(!showReplyForm)}
            onSave={() => setSaved(!saved)}
          />
          
          {showReplyForm && (
            <div className="mt-3">
              <CommentEditor 
                onSubmit={handleReplySubmit}
                placeholder="Write your reply..."
                buttonText="Reply"
              />
            </div>
          )}
          
          {comment.replies && comment.replies.map(reply => (
            <CommentItem 
              key={reply.id} 
              comment={reply} 
              isReply={true} 
              nestLevel={nestLevel + 1} 
              onReply={onReply} 
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CommentItem;
